export const M4_UNIT_13 = {
  id: "m4-u13-weather-forecast-planning",
  title: "Unidad 13: Weather Forecast & Planning / Pronóstico del Tiempo y Planificación",
  topic: "Learn to read weather forecasts and plan activities! Understand weather apps, talk about what weather is coming, make smart plans. Essential for daily life in Canada - all using present simple and continuous!",
  iconType: "forecast",
  grammarImage: "/grammar/grammar-m4-u13.jpg",
  grammarText: "THE FORECAST SAYS + weather (The forecast says sunny). IT IS + weather + TODAY/TOMORROW (It is sunny today, It is rainy tomorrow). WE CAN + verb (We can go to the park). WE CAN'T + verb (We can't go because it is raining). WHEN/BECAUSE + weather (When it rains, I stay home. I stay home because it is raining).",
  
  sets: [
    // SET 1: Basic Forecast Vocabulary
    {
      id: "m4-u13-set1",
      title: "Set 1: Understanding 'Forecast'",
      questions: [
        {
          type: "match",
          q: "Match forecast words to Spanish:",
          pairs: [
            { left: "forecast", right: "pronóstico" },
            { left: "today", right: "hoy" },
            { left: "tomorrow", right: "mañana" }
          ],
          a: "completed",
          tip: "Forecast = pronóstico! What the weather app tells you! Essential word!"
        },
        {
          type: "mc",
          q: "'The ___ says sunny tomorrow.' (pronóstico)",
          options: ["forecast", "forcast", "forescast"],
          a: "forecast",
          tip: "FORECAST! Spell carefully! One word. The weather forecast = el pronóstico del tiempo!"
        },
        {
          type: "input",
          q: "Complete: 'What does the ___ say?' (forecast)",
          a: "forecast"
        },
        {
          type: "order",
          q: "Ordena: 'El pronóstico dice lluvia'",
          words: ["The", "forecast", "says", "rain."],
          a: "The forecast says rain."
        },
        {
          type: "mc",
          q: "'It is sunny today. Tomorrow it is ___.' (reading forecast for tomorrow)",
          options: ["rainy (forecast says rainy)", "raining now", "rained yesterday"],
          a: "rainy (forecast says rainy)",
          successtip: "✓ PRESENT SIMPLE! Tomorrow as statement! 📅 'Tomorrow it IS rainy' = using present to state forecast! No future tense needed!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says ___ tomorrow.' (sunny/rainy/snowy)",
          a: "sunny / rainy / snowy"
        },
        {
          type: "mc",
          q: "'Today it is cold. The forecast says tomorrow it is ___.'",
          options: ["warm", "cold yesterday", "warming"],
          a: "warm",
          tip: "TODAY vs TOMORROW! Compare! Using IS for both because it's statement of fact/forecast!"
        },
        {
          type: "order",
          q: "Ordena: 'Hoy está soleado, mañana está nublado'",
          words: ["Today", "it", "is", "sunny,", "tomorrow", "it", "is", "cloudy."],
          a: "Today it is sunny, tomorrow it is cloudy.",
          successtip: "✓ COMPARING DAYS! Simple structure! ☀️→☁️ TODAY it is + TOMORROW it is = perfect for forecasts!"
        },
        {
          type: "input",
          q: "Complete: 'I check the ___ every morning.' (forecast)",
          a: "forecast"
        },
        {
          type: "mc",
          q: "Weather app shows: '☀️ Tomorrow'. This means:",
          options: [
            "Tomorrow it is sunny (forecast prediction)",
            "It is sunny right now",
            "Yesterday was sunny"
          ],
          a: "Tomorrow it is sunny (forecast prediction)",
          tip: "Reading weather apps! Icons + words = forecast! ☀️ = sunny, 🌧️ = rain, ❄️ = snow!"
        }
      ]
    },

    // SET 2: The Forecast Says...
    {
      id: "m4-u13-set2",
      title: "Set 2: 'The Forecast Says...'",
      questions: [
        {
          type: "match",
          q: "Match sentence structure:",
          pairs: [
            { left: "The forecast says sunny", right: "El pronóstico dice soleado" },
            { left: "The forecast says rain", right: "El pronóstico dice lluvia" },
            { left: "The forecast says 25 degrees", right: "El pronóstico dice 25 grados" }
          ],
          a: "completed",
          tip: "'The forecast SAYS' + weather = simple structure! No complicated grammar!"
        },
        {
          type: "mc",
          q: "'The forecast says ___.' (noun form - lluvia)",
          options: ["rain", "rainy", "raining"],
          a: "rain",
          tip: "'Says' + noun! Rain (noun) = lluvia. OR 'says rainy' (adjective). Both work!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast ___ snow tomorrow.' (says/is)",
          a: "says"
        },
        {
          type: "order",
          q: "Ordena: 'El pronóstico dice caluroso mañana'",
          words: ["The", "forecast", "says", "hot", "tomorrow."],
          a: "The forecast says hot tomorrow."
        },
        {
          type: "mc",
          q: "'The forecast says cold and ___.' (adding second weather)",
          options: ["windy", "wind", "winds"],
          a: "windy",
          successtip: "✓ TWO CONDITIONS! Natural! 🌬️ 'Cold AND windy' = two adjectives. Common pattern!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says ___ degrees.' (temperature number)",
          a: "20 / 25 / 30 / (any number)"
        },
        {
          type: "mc",
          q: "'The forecast says rain ___ afternoon.' (time)",
          options: ["this", "the", "a"],
          a: "this",
          tip: "'This afternoon' = esta tarde. 'This morning' = esta mañana. 'Tonight' = esta noche!"
        },
        {
          type: "order",
          q: "Ordena: 'El pronóstico dice lluvia esta noche'",
          words: ["The", "forecast", "says", "rain", "tonight."],
          a: "The forecast says rain tonight.",
          successtip: "✓ TONIGHT! Specific time! 🌙 Add time to forecast = more specific information!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast ___ sunny all week.' (says)",
          a: "says"
        },
        {
          type: "mc",
          q: "Reading app: 'Forecast: ⛈️ This afternoon, ☀️ Tomorrow'. You say:",
          options: [
            "'The forecast says a storm this afternoon, then sunny tomorrow'",
            "'It is storming now'",
            "'Yesterday was sunny'"
          ],
          a: "'The forecast says a storm this afternoon, then sunny tomorrow'",
          tip: "Reading icons! ⛈️ = storm, ☀️ = sunny. Describe what you see in app!"
        }
      ]
    },

    // SET 3: Can/Can't Based on Weather
    {
      id: "m4-u13-set3",
      title: "Set 3: 'We Can' & 'We Can't' with Weather",
      questions: [
        {
          type: "match",
          q: "Match weather to what you can/can't do:",
          pairs: [
            { left: "It is sunny", right: "We can go to the park" },
            { left: "It is raining", right: "We can't play outside" },
            { left: "It is snowing", right: "We can build a snowman" }
          ],
          a: "completed",
          tip: "CAN/CAN'T = ability/possibility! Weather determines what you CAN or CAN'T do!"
        },
        {
          type: "mc",
          q: "'It is sunny today. We ___ go to the beach!'",
          options: ["can", "can't", "cans"],
          a: "can",
          tip: "CAN = possible/able to! Good weather = CAN do outdoor activities!"
        },
        {
          type: "input",
          q: "Complete: 'It is raining. We ___ play soccer.' (can/can't)",
          a: "can't"
        },
        {
          type: "order",
          q: "Ordena: 'Está nevando, podemos esquiar'",
          words: ["It", "is", "snowing.", "We", "can", "go", "skiing."],
          a: "It is snowing. We can go skiing."
        },
        {
          type: "mc",
          q: "'The forecast says rain. We ___ have our picnic outside.'",
          options: ["can't", "can", "not can"],
          a: "can't",
          successtip: "✓ CAN'T! Weather prevents activity! 🚫 Rain = CAN'T have outdoor picnic. Logical consequence!"
        },
        {
          type: "input",
          q: "Complete: 'It is very cold. We ___ go skating!' (can - cold = good for skating)",
          a: "can"
        },
        {
          type: "mc",
          q: "'Tomorrow it is sunny. We ___ go hiking.'",
          options: ["can", "can't", "canning"],
          a: "can",
          tip: "TOMORROW + CAN! Planning! Using 'can' for tomorrow based on forecast!"
        },
        {
          type: "order",
          q: "Ordena: 'El pronóstico dice lluvia, no podemos tener BBQ'",
          words: ["The", "forecast", "says", "rain.", "We", "can't", "have", "a", "BBQ."],
          a: "The forecast says rain. We can't have a BBQ.",
          successtip: "✓ CAUSE AND EFFECT! Two sentences! 🌧️→🚫 Weather forecast → what we can't do. Natural connection!"
        },
        {
          type: "input",
          q: "Complete: 'It is hot. We ___ go swimming.' (can)",
          a: "can"
        },
        {
          type: "mc",
          q: "'It is too cold today. We ___ run outside.'",
          options: ["can't", "can", "not"],
          a: "can't",
          tip: "'Too cold' = demasiado frío = CAN'T do activity comfortably! TOO + adjective + CAN'T!"
        }
      ]
    },

    // SET 4: Planning Activities with Weather
    {
      id: "m4-u13-set4",
      title: "Set 4: Making Plans",
      questions: [
        {
          type: "match",
          q: "Match planning phrases:",
          pairs: [
            { left: "Let's check the forecast", right: "Revisemos el pronóstico" },
            { left: "We can go tomorrow", right: "Podemos ir mañana" },
            { left: "The weather is good", right: "El clima está bien" }
          ],
          a: "completed",
          tip: "'Let's' = vamos a (suggestion)! 'Let's check' = common planning phrase!"
        },
        {
          type: "mc",
          q: "'___ check the forecast before we go.' (suggestion)",
          options: ["Let's", "Let", "Lets"],
          a: "Let's",
          tip: "LET'S = let us! Apostrophe needed! 'Let's check' = vamos a revisar!"
        },
        {
          type: "input",
          q: "Complete: 'What ___ the weather tomorrow?' (is)",
          a: "is"
        },
        {
          type: "order",
          q: "Ordena: 'Revisemos el clima antes de decidir'",
          words: ["Let's", "check", "the", "weather", "before", "we", "decide."],
          a: "Let's check the weather before we decide."
        },
        {
          type: "mc",
          q: "'Tomorrow is sunny. ___ go to the park!'",
          options: ["Let's", "We", "I"],
          a: "Let's",
          successtip: "✓ LET'S! Suggestion based on forecast! ☀️ Good weather = suggestion to do activity!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says rain. ___ stay home.' (Let's/We can)",
          a: "Let's / We can"
        },
        {
          type: "mc",
          q: "'The weather is good tomorrow. We ___ planning a BBQ.' (present continuous - planning)",
          options: ["are", "is", "am"],
          a: "are",
          tip: "PRESENT CONTINUOUS for plans! 'We ARE planning' = estamos planeando (future plan, present continuous)!"
        },
        {
          type: "order",
          q: "Ordena: 'Estamos planeando un picnic para el sábado'",
          words: ["We", "are", "planning", "a", "picnic", "for", "Saturday."],
          a: "We are planning a picnic for Saturday.",
          successtip: "✓ PRESENT CONTINUOUS! Future plans! 📅 'We are planning' = present continuous for future arrangements!"
        },
        {
          type: "input",
          q: "Complete: 'I am ___ the forecast now.' (checking - present continuous)",
          a: "checking"
        },
        {
          type: "mc",
          q: "'What are you doing?' - 'I ___ the weather for tomorrow.'",
          options: ["am checking", "check", "checks"],
          a: "am checking",
          tip: "RIGHT NOW action! Present continuous! 'I am checking' = estoy revisando (happening now)!"
        }
      ]
    },

    // SET 5: Weather Apps & Information
    {
      id: "m4-u13-set5",
      title: "Set 5: Reading Weather Apps",
      questions: [
        {
          type: "match",
          q: "Match weather app terms:",
          pairs: [
            { left: "high", right: "máxima temperatura" },
            { left: "low", right: "mínima temperatura" },
            { left: "chance of rain", right: "probabilidad de lluvia" }
          ],
          a: "completed",
          tip: "HIGH = máxima, LOW = mínima! These are on every weather app!"
        },
        {
          type: "mc",
          q: "App shows: 'High: 25°, Low: 15°'. This means:",
          options: [
            "Maximum temperature is 25, minimum is 15",
            "It is 25 degrees all day",
            "High is bad, low is good"
          ],
          a: "Maximum temperature is 25, minimum is 15",
          tip: "HIGH = hottest point (usually afternoon). LOW = coldest point (usually morning/night)!"
        },
        {
          type: "input",
          q: "Complete: 'The ___ today is 28 degrees.' (high = máxima)",
          a: "high"
        },
        {
          type: "order",
          q: "Ordena: 'La máxima hoy es 22 grados'",
          words: ["The", "high", "today", "is", "22", "degrees."],
          a: "The high today is 22 degrees."
        },
        {
          type: "mc",
          q: "App shows: 'Rain: 70%'. This means:",
          options: [
            "There is a 70% chance it is raining / it rains",
            "It is raining 70 minutes",
            "70 people think it is raining"
          ],
          a: "There is a 70% chance it is raining / it rains",
          successtip: "✓ PERCENTAGE! Probability! 📊 70% = high chance! Over 50% = probably rains. Under 30% = probably not!"
        },
        {
          type: "input",
          q: "Complete: 'There is a 60% ___ of rain.' (chance)",
          a: "chance"
        },
        {
          type: "mc",
          q: "App shows: '🌧️ 80%'. You say:",
          options: [
            "'There is a high chance of rain. I need an umbrella!'",
            "'It is not raining'",
            "'No rain today'"
          ],
          a: "'There is a high chance of rain. I need an umbrella!'",
          tip: "80% = HIGH chance! Take umbrella! Over 70% = prepare for rain!"
        },
        {
          type: "order",
          q: "Ordena: 'Hay 20% de probabilidad de lluvia - está bien'",
          words: ["There", "is", "a", "20%", "chance", "of", "rain", "-", "that's", "okay."],
          a: "There is a 20% chance of rain - that's okay.",
          successtip: "✓ LOW PERCENTAGE! Good news! ✓ 20% = low chance! Probably NOT raining. Safe to plan outdoor activity!"
        },
        {
          type: "input",
          q: "Complete: 'Tomorrow the low is 5 ___.' (degrees)",
          a: "degrees"
        },
        {
          type: "mc",
          q: "Reading full forecast: 'Tomorrow: ☀️ High 26°, Low 18°, Rain 10%'. You understand:",
          options: [
            "Sunny day, warm (26°), mild night (18°), probably no rain (10%)",
            "It is raining all day",
            "It is cold"
          ],
          a: "Sunny day, warm (26°), mild night (18°), probably no rain (10%)",
          tip: "Reading complete forecast! Icon + temperatures + rain chance = full picture! This is essential skill!"
        }
      ]
    },

    // SET 6: Because & When with Weather
    {
      id: "m4-u13-set6",
      title: "Set 6: Explaining with 'Because' & 'When'",
      questions: [
        {
          type: "match",
          q: "Match connecting words:",
          pairs: [
            { left: "because", right: "porque (reason)" },
            { left: "when", right: "cuando (time)" },
            { left: "so", right: "así que (result)" }
          ],
          a: "completed",
          tip: "BECAUSE = porque (reason). WHEN = cuando (time). Connect weather to actions!"
        },
        {
          type: "mc",
          q: "'I am staying home ___ it is raining.' (reason)",
          options: ["because", "when", "but"],
          a: "because",
          tip: "BECAUSE = porque! Gives reason! 'BECAUSE it is raining' = por qué me quedo en casa!"
        },
        {
          type: "input",
          q: "Complete: 'We can't go outside ___ it is too cold.' (because)",
          a: "because"
        },
        {
          type: "order",
          q: "Ordena: 'Me quedo adentro porque está nevando'",
          words: ["I", "am", "staying", "inside", "because", "it", "is", "snowing."],
          a: "I am staying inside because it is snowing."
        },
        {
          type: "mc",
          q: "'___ it is sunny, I am happy.' (time/condition)",
          options: ["When", "Because", "But"],
          a: "When",
          successtip: "✓ WHEN! Time/condition! ☀️ 'WHEN it is sunny' = cuando está soleado. Pattern: WHEN + weather + result!"
        },
        {
          type: "input",
          q: "Complete: 'When it rains, I ___ an umbrella.' (need/use)",
          a: "need / use / take / carry"
        },
        {
          type: "mc",
          q: "'I am wearing a jacket ___ it is cold.'",
          options: ["because", "when", "and"],
          a: "because",
          tip: "BECAUSE explains WHY! Why am I wearing jacket? BECAUSE cold! Reason = BECAUSE!"
        },
        {
          type: "order",
          q: "Ordena: 'Cuando hace calor, vamos a la playa'",
          words: ["When", "it", "is", "hot,", "we", "go", "to", "the", "beach."],
          a: "When it is hot, we go to the beach.",
          successtip: "✓ WHEN pattern! Habitual! 🏖️ 'When hot → beach' = general pattern (present simple after WHEN for habits)!"
        },
        {
          type: "input",
          q: "Complete: 'I am happy ___ the weather is nice.' (when/because)",
          a: "when / because"
        },
        {
          type: "mc",
          q: "'It is raining, ___ we are staying home.' (result)",
          options: ["so", "because", "when"],
          a: "so",
          tip: "SO = así que (result/therefore)! Weather SO result. 'Raining SO staying home' = consequence!"
        }
      ]
    },

    // SET 7: More Weather Conditions for Forecasts
    {
      id: "m4-u13-set7",
      title: "Set 7: Advanced Weather Words",
      questions: [
        {
          type: "match",
          q: "Match weather conditions:",
          pairs: [
            { left: "foggy", right: "con niebla" },
            { left: "stormy", right: "tormentoso" },
            { left: "icy", right: "helado" }
          ],
          a: "completed",
          tip: "More specific weather words! Important for safety and planning!"
        },
        {
          type: "mc",
          q: "'The forecast says ___ tomorrow. The roads are dangerous.' (niebla)",
          options: ["fog / foggy", "sun", "hot"],
          a: "fog / foggy",
          tip: "FOG (noun) = niebla. FOGGY (adjective) = neblinoso. Both work with 'says'!"
        },
        {
          type: "input",
          q: "Complete: 'It is ___ outside. I can't see the road.' (foggy)",
          a: "foggy"
        },
        {
          type: "order",
          q: "Ordena: 'El pronóstico dice tormenta esta noche'",
          words: ["The", "forecast", "says", "a", "storm", "tonight."],
          a: "The forecast says a storm tonight."
        },
        {
          type: "mc",
          q: "'The roads are ___. Drive carefully!' (heladas)",
          options: ["icy", "ice", "icing"],
          a: "icy",
          successtip: "✓ ICY! Dangerous! 🧊 ICY roads = resbalosas! Very dangerous for driving. Stay home or drive slow!"
        },
        {
          type: "input",
          q: "Complete: 'It is very ___ today. I can hear thunder.' (stormy)",
          a: "stormy"
        },
        {
          type: "mc",
          q: "'The forecast says freezing rain. This means:",
          options: [
            "Very dangerous - rain that freezes on surfaces",
            "Normal cold rain",
            "Snow"
          ],
          a: "Very dangerous - rain that freezes on surfaces",
          tip: "FREEZING RAIN = most dangerous Canadian weather! Rain turns to ice when it touches ground! Don't drive!"
        },
        {
          type: "order",
          q: "Ordena: 'Está muy ventoso - no puedo caminar bien'",
          words: ["It", "is", "very", "windy", "-", "I", "can't", "walk", "straight."],
          a: "It is very windy - I can't walk straight.",
          successtip: "✓ VERY WINDY! Strong wind! 💨 'Can't walk straight' = no puedo caminar derecho. Real effect of wind!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says ___ and lightning.' (thunder)",
          a: "thunder"
        },
        {
          type: "mc",
          q: "'There is a thunderstorm coming. We ___ stay inside.'",
          options: ["need to / should", "want", "are"],
          a: "need to / should",
          tip: "NEED TO = necesitar. Thunderstorm = dangerous! Need to stay safe inside!"
        }
      ]
    },

    // SET 8: Checking Forecasts - Real Situations
    {
      id: "m4-u13-set8",
      title: "Set 8: Real Forecast Conversations",
      questions: [
        {
          type: "mc",
          q: "Friend: 'Are we going to the park?' You: 'I don't know. ___ the forecast first.'",
          options: ["Let me check", "I check", "Checking"],
          a: "Let me check",
          tip: "'Let me...' = déjame... Very common phrase! 'Let me check' = déjame revisar!"
        },
        {
          type: "input",
          q: "Complete: 'I am ___ the weather app now.' (checking - present continuous)",
          a: "checking"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué dice el pronóstico para mañana?'",
          words: ["What", "does", "the", "forecast", "say", "for", "tomorrow?"],
          a: "What does the forecast say for tomorrow?"
        },
        {
          type: "mc",
          q: "'What does the forecast say?' - You look at app and answer: '___'",
          options: [
            "'It says sunny and 25 degrees!'",
            "'Yes'",
            "'Forecast'"
          ],
          a: "'It says sunny and 25 degrees!'",
          successtip: "✓ ANSWER WITH INFO! Full response! 📱 Read app → give weather details. Natural conversation!"
        },
        {
          type: "input",
          q: "Complete: 'My app ___ rain, but yours says sunny!' (says)",
          a: "says"
        },
        {
          type: "mc",
          q: "Two different apps show different forecasts. You say:",
          options: [
            "'Let's check one more app to be sure'",
            "'Apps are always right'",
            "'Don't check'"
          ],
          a: "'Let's check one more app to be sure'",
          tip: "Different apps = different predictions! Check multiple sources! Very Canadian habit!"
        },
        {
          type: "order",
          q: "Ordena: 'Déjame revisar mi aplicación del clima'",
          words: ["Let", "me", "check", "my", "weather", "app."],
          a: "Let me check my weather app."
        },
        {
          type: "input",
          q: "Complete: 'The app ___ the weather every hour.' (updates = actualiza)",
          a: "updates"
        },
        {
          type: "mc",
          q: "'I check the forecast ___ morning before work.' (frequency)",
          options: ["every", "all", "many"],
          a: "every",
          successtip: "✓ EVERY! Routine! 📅 'Every morning' = todas las mañanas. Present simple for habits!"
        },
        {
          type: "mc",
          q: "Canadian habit: Before any outdoor plan, first step is:",
          options: [
            "Check the forecast",
            "Buy tickets",
            "Invite friends"
          ],
          a: "Check the forecast",
          tip: "ALWAYS check forecast FIRST! This is Canadian culture! Weather determines everything!"
        }
      ]
    },

    // SET 9: Backup Plans & Alternatives
    {
      id: "m4-u13-set9",
      title: "Set 9: Plan A and Plan B",
      questions: [
        {
          type: "match",
          q: "Match planning vocabulary:",
          pairs: [
            { left: "backup plan", right: "plan alternativo (Plan B)" },
            { left: "just in case", right: "por si acaso" },
            { left: "or", right: "o (alternative)" }
          ],
          a: "completed",
          tip: "Backup plan = Plan B! Always have one when weather is uncertain!"
        },
        {
          type: "mc",
          q: "'The forecast says 50% rain. We need a ___ plan.' (Plan B)",
          options: ["backup", "back", "second"],
          a: "backup",
          tip: "BACKUP PLAN! 50% = uncertain! Need alternative plan if weather is bad!"
        },
        {
          type: "input",
          q: "Complete: 'I am taking an umbrella ___ in case.' (just = por si)",
          a: "just"
        },
        {
          type: "order",
          q: "Ordena: 'Necesitamos un plan alternativo por si acaso'",
          words: ["We", "need", "a", "backup", "plan", "just", "in", "case."],
          a: "We need a backup plan just in case."
        },
        {
          type: "mc",
          q: "'We can go to the beach ___ we can go to the mall.' (alternative)",
          options: ["or", "and", "but"],
          a: "or",
          successtip: "✓ OR! Alternative! 🏖️ or 🏬 Beach (good weather) OR mall (bad weather). Two options!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says rain. Let's have the party ___ or outside?' (inside)",
          a: "inside"
        },
        {
          type: "mc",
          q: "'We are planning an outdoor BBQ, but ___ it rains, we are moving it inside.'",
          options: [
            "if (conditional)",
            "when",
            "because"
          ],
          a: "if (conditional)",
          tip: "IF = si (condition). Using 'if' with present simple! 'If it rains' = si llueve. Simple conditional!"
        },
        {
          type: "order",
          q: "Ordena: 'Si llueve, vamos al cine en su lugar'",
          words: ["If", "it", "rains,", "we", "are", "going", "to", "the", "movies", "instead."],
          a: "If it rains, we are going to the movies instead.",
          successtip: "✓ IF + PRESENT! Conditional planning! 🎬 IF rain (present) → movies (continuous for plan). Natural structure!"
        },
        {
          type: "input",
          q: "Complete: 'I always carry an umbrella ___ in case it rains.' (just)",
          a: "just"
        },
        {
          type: "mc",
          q: "Smart planning: 'The forecast says 60% rain. What do we do?'",
          options: [
            "'We have a backup plan - we can do something indoors'",
            "'We cancel everything'",
            "'We ignore the forecast'"
          ],
          a: "'We have a backup plan - we can do something indoors'",
          tip: "60% = high chance! Need backup! Indoor alternative = smart Canadian planning!"
        }
      ]
    },

    // SET 10: Weather and Daily Decisions
    {
      id: "m4-u13-set10",
      title: "Set 10: Daily Weather Decisions",
      questions: [
        {
          type: "mc",
          q: "'It is snowing heavily. Many people ___ from home today.' (working)",
          options: ["are working", "work", "is working"],
          a: "are working",
          tip: "Present continuous for TODAY! 'Are working from home' = están trabajando desde casa (today's decision)!"
        },
        {
          type: "input",
          q: "Complete: 'The forecast says freezing rain. I ___ taking the bus.' (am not)",
          a: "am not / 'm not"
        },
        {
          type: "order",
          q: "Ordena: 'Está demasiado helado para caminar - tomo el bus'",
          words: ["It", "is", "too", "icy", "to", "walk", "-", "I", "am", "taking", "the", "bus."],
          a: "It is too icy to walk - I am taking the bus."
        },
        {
          type: "mc",
          q: "'Tomorrow is very cold. I ___ my winter coat.' (decision for tomorrow)",
          options: ["am wearing", "wear", "wears"],
          a: "am wearing",
          successtip: "✓ PRESENT CONTINUOUS! Future plan! 🧥 'Am wearing tomorrow' = present continuous for future arrangement!"
        },
        {
          type: "input",
          q: "Complete: 'It is raining. Everyone ___ umbrellas.' (is carrying - present continuous)",
          a: "is carrying / has"
        },
        {
          type: "mc",
          q: "'The forecast says a storm. Schools ___ closing early today.'",
          options: ["are", "is", "am"],
          a: "are",
          tip: "Present continuous for current decision! Schools ARE closing = están cerrando (happening now/decision made)!"
        },
        {
          type: "order",
          q: "Ordena: 'Hay alerta de tormenta - mi jefe dice que podemos irnos temprano'",
          words: ["There", "is", "a", "storm", "warning", "-", "my", "boss", "says", "we", "can", "leave", "early."],
          a: "There is a storm warning - my boss says we can leave early.",
          successtip: "✓ REAL SITUATION! Safety! 🚨 Storm warning → boss decision → CAN leave. This happens in Canada!"
        },
        {
          type: "input",
          q: "Complete: 'It is too hot. I ___ working outside today.' (am not)",
          a: "am not / 'm not"
        },
        {
          type: "mc",
          q: "'The roads are icy. I ___ drive to work.' (negative ability)",
          options: ["can't", "can", "not"],
          a: "can't",
          tip: "CAN'T = no puedo! Icy roads = dangerous = CAN'T drive safely!"
        },
        {
          type: "mc",
          q: "COMPLETE CONVERSATION:\n\nBoss: 'Did you check the forecast for tomorrow?'\nYou: 'Yes! It ___ snow tomorrow.'\nBoss: 'How much snow?'\nYou: 'The forecast says 30 centimeters!'\nBoss: 'Okay. If it is snowing heavily, you ___ work from home.'\nYou: 'Thank you! I am checking the forecast again tonight ___.'",
        options: [
          "says / can / just in case",
          "is / are / because",
          "say / working / tomorrow"
        ],
        a: "says / can / just in case",
        successtip: "✓ COMPLETE MASTERY! Real workplace conversation! 💼 Check forecast → discuss → make plan → verify later. This IS daily Canadian life! Perfect! 🎯"
      }
      ]
    }
  ],

// ===================================================================
// READING SET: Maria and the Weather Forecast
// ===================================================================
readingSet: {
  id: "m4-u13-reading",
  title: "Reading: Planning and the Forecast",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Importance of Checking (preguntas 1 y 2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Why Check the Forecast?**

Hi! I'm Maria. I want to tell you about something important in Canada—checking the weather forecast! Every morning, I check the forecast before I get dressed. Why? Because the weather in Canada changes A LOT! 

Sometimes the forecast says sunny, but then it rains! Sometimes it says cold, but then it is warm! It helps me decide what to wear and how to plan my day.`
    },
    {
      type: "mc",
      q: "En la frase 'The forecast says sunny', ¿qué tiempo verbal se está utilizando?",
      options: [
        "Presente simple (para reportar lo que muestra el pronóstico)",
        "Pasado",
        "Futuro"
      ],
      a: "Presente simple (para reportar lo que muestra el pronóstico)",
      tip: "Usamos el presente simple para decir lo que 'dice' un app o un libro. 📱",
      successtip: "✓ PRESENT SIMPLE!\n'The app says...' es la forma correcta de reportar información actual. 🎯"
    },
    {
      type: "mc",
      q: "¿Por qué María considera que revisar el clima es tan importante en Canadá?",
      options: [
        "Porque el clima cambia mucho y muy rápido",
        "Porque le gusta mirar su teléfono",
        "Porque siempre hace frío"
      ],
      a: "Porque el clima cambia mucho y muy rápido",
      successtip: "✓ UNPREDICTABLE WEATHER!\nEn Canadá, el clima de la mañana puede ser distinto al de la tarde. 🌦️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Saturday Picnic Plan (preguntas 3, 4, 5 y 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Plan and the Changes**

Last Saturday, I am planning a picnic with my friends. On Friday night, I check my weather app. It says sunny and 25 degrees for Saturday. Perfect! 

Saturday morning, I check again. Now it says cloudy with a 40% chance of rain! I text my friends: "The forecast is changing! If it rains, we are going to my apartment instead." At 1pm, I check ONE MORE TIME. Now it says 60% chance of rain! I tell everyone: "Let's meet at my place."`
    },

    

    {
      type: "mc",
      q: "¿Cuántas veces revisó María el pronóstico específicamente para el plan del sábado?",
      options: [
        "Tres veces (viernes noche, sábado mañana y sábado 1pm)",
        "Una sola vez",
        "Ninguna"
      ],
      a: "Tres veces (viernes noche, sábado mañana y sábado 1pm)",
      successtip: "✓ REPETITIVE CHECKING!\nRevisar varias veces es clave cuando hay planes al aire libre. ⌚"
    },
    {
      type: "mc",
      q: "En 'I am planning a picnic', ¿por qué usa el presente continuo (-ing)?",
      options: [
        "Para hablar de un arreglo o plan futuro",
        "Porque está ocurriendo justo ahora mientras habla",
        "Es una acción del pasado"
      ],
      a: "Para hablar de un arreglo o plan futuro",
      successtip: "✓ FUTURE ARRANGEMENTS!\nEl presente continuo es muy común para planes ya coordinados. 📅"
    },
    {
      type: "mc",
      q: "En la frase 'If it rains, we are going to my apartment', ¿cuál es la estructura?",
      options: [
        "IF + presente simple + presente continuo (para plan futuro)",
        "Todo en tiempo pasado",
        "Todo en tiempo futuro con 'will'"
      ],
      a: "IF + presente simple + presente continuo (para plan futuro)",
      successtip: "✓ CONDITIONAL PLAN!\nUna estructura muy natural para planes alternativos. 🏠"
    },
    {
      type: "mc",
      q: "¿Qué significa que necesiten un 'backup plan'?",
      options: [
        "Un plan alternativo (Plan B) por si el clima es malo",
        "Que necesitan regresar a casa",
        "Que deben cancelar todo"
      ],
      a: "Un plan alternativo (Plan B) por si el clima es malo",
      successtip: "✓ PLAN B!\nUn concepto esencial para sobrevivir a los cambios sociales en Canadá. 🔄"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: The Result and the New Rule (preguntas 8, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Outcome**

At 2pm, we are all at my apartment. We are having fun. Then, at 3pm, I look outside. IT IS SUNNY! No rain! We all laugh. The forecast was wrong! 

But now I am smarter. When I check the forecast and it says 50% or more rain, I am planning something indoors. When it says less than 30%, I am planning something outdoors. My Canadian friends laugh, but it's true: in Canada, planning is everything!`
    },
    {
      type: "mc",
      q: "¿Cuál es la nueva regla de María para el éxito de sus planes?",
      options: [
        "50% o más de lluvia = plan en interiores",
        "Ir afuera sin importar la lluvia",
        "Cancelar todo si hay nubes"
      ],
      a: "50% o más de lluvia = plan en interiores",
      successtip: "✓ SMART PLANNING!\nBasar las decisiones en porcentajes de probabilidad es muy útil. 💡"
    },
    {
      type: "mc",
      q: "¿Qué sucedió finalmente a las 3pm cuando estaban en el departamento?",
      options: [
        "Salió el sol (el pronóstico falló, no llovió)",
        "Empezó a llover muy fuerte",
        "Empezó a nevar"
      ],
      a: "Salió el sol (el pronóstico falló, no llovió)",
      successtip: "✓ WRONG FORECAST!\nA veces el clima nos sorprende a pesar de la tecnología. ☀️"
    },
    {
      type: "mc",
      q: "¿Cuál es la lección principal que María aprendió?",
      options: [
        "Tener siempre un plan de respaldo porque el pronóstico no es perfecto",
        "Nunca revisar el clima",
        "No hacer planes con amigos"
      ],
      a: "Tener siempre un plan de respaldo porque el pronóstico no es perfecto",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Has aprendido sobre la importancia del Plan B y el uso del presente para el futuro. 🏆"
    }
  ]
},

  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u13-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match key phrases:",
        pairs: [
          { left: "The forecast says", right: "El pronóstico dice" },
          { left: "Let's check", right: "Revisemos" },
          { left: "We can/can't", right: "Podemos/No podemos" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "'The forecast ___ rain tomorrow.' (verb)",
        options: ["says", "say", "saying"],
        a: "says"
      },
      {
        type: "mc",
        q: "Weather app vocabulary: Maximum temperature =",
        options: ["high", "low", "chance"],
        a: "high"
      },
      {
        type: "input",
        q: "Complete: 'It is raining. We ___ go to the park.' (can't)",
        a: "can't"
      },
      {
        type: "input",
        q: "Complete: 'I am staying home ___ it is snowing.' (because)",
        a: "because"
      },
      {
        type: "order",
        q: "Ordena: 'Revisemos el pronóstico antes de decidir'",
        words: ["Let's", "check", "the", "forecast", "before", "we", "decide."],
        a: "Let's check the forecast before we decide."
      },
      {
        type: "mc",
        q: "'Backup plan' means:",
        options: ["Plan B / alternative plan", "First plan", "No plan"],
        a: "Plan B / alternative plan"
      },
      {
        type: "mc",
        q: "'60% chance of rain' means:",
        options: [
          "High probability - probably rains",
          "Definitely no rain",
          "60 minutes of rain"
        ],
        a: "High probability - probably rains"
      },
      {
        type: "mc",
        q: "Present continuous for plans: 'Tomorrow we ___ a picnic.'",
        options: ["are having", "have", "has"],
        a: "are having",
        tip: "Present continuous for future arrangements! 'Are having tomorrow' = future plan!"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Complete real conversation:\n\nFriend: 'Let's go hiking tomorrow!'\nYou: 'Good idea! Let me ___ the forecast first.'\n[You check phone]\nYou: 'Hmm. The forecast ___ rain tomorrow. There is a 70% chance.'\nFriend: 'Oh no! ___ we go?'\nYou: 'I don't think so. We ___ go because it is raining. Let's have a ___ plan.'\nFriend: 'Good idea! If it rains, we ___ go to the museum instead.'\nYou: 'Perfect! I am ___ the forecast again tomorrow morning just in case.'\nFriend: 'Smart! You are learning to be Canadian!'\n\nCorrect words:",
        options: [
          "check / says / Can / can't / backup / can / checking",
          "see / is / Do / can / second / are / check",
          "look / say / Are / not / other / go / looked"
        ],
        a: "check / says / Can / can't / backup / can / checking",
        successtip: "✓ UNIT 13 COMPLETE! Weather forecast mastery! 🎉 Using ONLY present simple, present continuous, and CAN! No future tense needed! You can now: read forecasts, understand weather apps, use 'The forecast says', plan with CAN/CAN'T, explain with BECAUSE/WHEN, make backup plans, use present continuous for future plans! This is REAL Canadian life skill using your CURRENT grammar! From zero to reading weather apps and planning like a local! 🇨🇦📱☀️🌧️❄️"
      }
    ]
  }
};
